/**
 * The checkout delivery preview's refusals, bound to the inputs they name
 * (`.claude/rules/api-validation.md`), and the delivery prices that changed under a
 * checkout for the CSR to confirm (docs/features/order-to-invoice/
 * 44_catalogue_step3_update_spec.md §5.4). *Change delivery…* on an order reads them.
 *
 * Headless: every sentence shown is the backend's. A refusal on a line
 * (`lines.2.is_optional`) names no input in the dialog, so it joins the
 * banner in the server's own words.
 */

import { ApiError } from "../data/api/errors";
import {
  OrderStepNotOfferedError,
  UnsafeActionUrlError,
} from "../data/order-to-invoice-actions";
import { extractFieldErrors } from "../data/validation";

/** The checkout dialog's and the basket's own inputs a refusal can name. */
const BASKET_FIELDS = new Set([
  "payment_method",
  "customer_order_number",
  "required_on",
  "contact_person_id",
  "person_id",
  "quantity",
  "quantities",
  "is_optional",
  "cc_emails",
  "expires_at",
] as const);
export type BasketField = typeof BASKET_FIELDS extends Set<infer F> ? F : never;

/**
 * The Delivery section's inputs a refusal can name (`deliveries.2.agreed_delivery_reason`), bound next to that
 * delivery's input. The index is the delivery's place in the `deliveries` the checkout sent.
 */
const DELIVERY_FIELDS = new Set([
  "service_level",
  "collect",
  "collection_point_address_id",
  "agreed_delivery_price_pence",
  "agreed_delivery_reason",
  "recipient_name",
  "recipient_phone",
] as const);
export type DeliveryField =
  typeof DELIVERY_FIELDS extends Set<infer F> ? F : never;

export interface DeliveryFieldError {
  /** Index into the `deliveries` the checkout sent. */
  index: number;
  field: DeliveryField;
  message: string;
}

/** A refusal's delivery errors by the delivery they name, given the refs in the order the checkout sent them. */
export function deliveryErrorsByRef(
  errors: ReadonlyArray<DeliveryFieldError>,
  sentRefs: readonly string[],
): Record<string, Partial<Record<DeliveryField, string>>> {
  const byRef: Record<string, Partial<Record<DeliveryField, string>>> = {};
  for (const e of errors) {
    const ref = sentRefs[e.index];
    if (ref === undefined) continue;
    byRef[ref] = { ...byRef[ref], [e.field]: e.message };
  }
  return byRef;
}

/** A line whose price moved between the basket and checkout. */
export interface ChangedPrice {
  /** 1-based, as the basket lists its lines. */
  position: number;
  oldPricePence: number;
  newPricePence: number;
}

/** A delivery whose price is no longer the one the Delivery section showed (null: it showed none, or has none now). */
export interface ChangedDeliveryPrice {
  deliveryRef: string;
  oldPricePence: number | null;
  newPricePence: number | null;
}

export interface BasketFailure {
  /** The banner: the server's words for what no input holds, and a count when inputs are highlighted. */
  summary: string;
  /** The backend's message for each input it refused, to show beside that input. */
  fieldErrors: Partial<Record<BasketField, string>>;
  /** The backend's message for each Delivery section input it refused. */
  deliveryErrors: DeliveryFieldError[];
  /** Set when checkout stopped because prices changed: the basket now holds the new prices. */
  changedPrices: ChangedPrice[] | null;
  /** Set when checkout stopped because a delivery's price is no longer the one shown: the section reads again. */
  changedDeliveries: ChangedDeliveryPrice[] | null;
}

/** A failure that is only a sentence: nothing to bind to an input, no price moved. */
export function plainBasketFailure(summary: string): BasketFailure {
  return {
    summary,
    fieldErrors: {},
    deliveryErrors: [],
    changedPrices: null,
    changedDeliveries: null,
  };
}

function fieldOf(field: string): BasketField | null {
  // `quantities.1` is the quantity options input; `cc_emails.0` the copy list.
  const input = field.split(".")[0] ?? field;
  return BASKET_FIELDS.has(input as BasketField)
    ? (input as BasketField)
    : null;
}

interface PricesChangedDetail {
  reason_code?: unknown;
  changes?: Array<{
    position: number;
    old_price_pence: number;
    new_price_pence: number;
  }>;
  delivery_changes?: Array<{
    delivery_ref: string;
    old_price_pence: number | null;
    new_price_pence: number | null;
  }>;
}

/** The 409 `prices_changed` a checkout answers: the lines and the deliveries whose price is not the one shown. */
function pricesChangedOf(err: unknown): {
  changedPrices: ChangedPrice[] | null;
  changedDeliveries: ChangedDeliveryPrice[] | null;
} {
  const none = { changedPrices: null, changedDeliveries: null };
  if (!(err instanceof ApiError) || err.status !== 409) return none;
  const detail = (err.body as { detail?: unknown } | null)?.detail as
    PricesChangedDetail | undefined;
  if (detail?.reason_code !== "prices_changed") return none;
  return {
    changedPrices:
      detail.changes?.map((c) => ({
        position: c.position,
        oldPricePence: c.old_price_pence,
        newPricePence: c.new_price_pence,
      })) ?? null,
    changedDeliveries:
      detail.delivery_changes?.map((c) => ({
        deliveryRef: c.delivery_ref,
        oldPricePence: c.old_price_pence,
        newPricePence: c.new_price_pence,
      })) ?? null,
  };
}

/** `deliveries.2.agreed_delivery_reason` → the third delivery's reason; null for any other field. */
function deliveryInputOf(
  field: string,
): { index: number; field: DeliveryField } | null {
  const match = /^deliveries\.(\d+)\.(\w+)$/.exec(field);
  if (!match || !DELIVERY_FIELDS.has(match[2] as DeliveryField)) return null;
  return { index: Number(match[1]), field: match[2] as DeliveryField };
}

/**
 * What to show when a basket step or a checkout is refused: a refusal on
 * one of the dialog's inputs is bound to it and counted in the banner;
 * anything else (a line that offers a choice, a basket already checked out,
 * prices that changed) is the server's own message, verbatim.
 */
export function basketFailure(err: unknown): BasketFailure {
  const fieldErrors: BasketFailure["fieldErrors"] = {};
  const deliveryErrors: DeliveryFieldError[] = [];
  const unbound: string[] = [];
  for (const e of extractFieldErrors(err)) {
    const input = fieldOf(e.field);
    const deliveryInput = deliveryInputOf(e.field);
    if (input) fieldErrors[input] ??= e.message;
    else if (deliveryInput)
      deliveryErrors.push({ ...deliveryInput, message: e.message });
    else unbound.push(e.message);
  }
  const count = Object.keys(fieldErrors).length + deliveryErrors.length;
  const counted =
    count === 0
      ? null
      : count === 1
        ? "One field needs correcting."
        : `${count} fields need correcting.`;
  const changed = pricesChangedOf(err);
  if (counted || unbound.length > 0) {
    return {
      // The Delivery section's inputs sit behind the checkout dialog, so their sentences are said here as well.
      summary: [...unbound, ...deliveryErrors.map((e) => e.message), counted]
        .filter(Boolean)
        .join(" "),
      fieldErrors,
      deliveryErrors,
      ...changed,
    };
  }
  return {
    summary: serverMessage(err),
    fieldErrors,
    deliveryErrors,
    ...changed,
  };
}

function serverMessage(err: unknown): string {
  if (
    err instanceof OrderStepNotOfferedError ||
    err instanceof UnsafeActionUrlError
  )
    return err.message;
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string") return detail;
    if (
      detail &&
      typeof detail === "object" &&
      typeof (detail as { message?: unknown }).message === "string"
    )
      return (detail as { message: string }).message;
  }
  return err instanceof Error
    ? err.message
    : "Could not do that — try again shortly.";
}
